import { useMemo } from 'react';
import { Skeleton, Button, Progress } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, StatusPill, EmptyState } from '@vnetwork/ui/data';
import { useSlaDashboardOverview } from '@/api/dashboard-personal';
import { WidgetCard } from './WidgetCard';

/** First/last day of the current month as YYYY-MM-DD, plus a display label. */
function monthBounds(): { start: string; end: string; label: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n: number) => String(n).padStart(2, '0');
  const start = `${y}-${pad(m + 1)}-01`;
  const last = new Date(y, m + 1, 0).getDate();
  const end = `${y}-${pad(m + 1)}-${pad(last)}`;
  return { start, end, label: `01/${pad(m + 1)} – ${last}/${pad(m + 1)}/${y}` };
}

/** Minutes → "Xh Ym" / "Xh" / "Ym" / "0m". */
function fmtMinutes(min?: number): string {
  const m = Math.max(0, Math.round(min ?? 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h < 24) return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}

/** Uptime % → progress tone: ≥99.5 success, ≥99 warning, else danger. */
function uptimeTone(pct: number): 'success' | 'warning' | 'danger' {
  if (pct >= 99.5) return 'success';
  if (pct >= 99) return 'warning';
  return 'danger';
}

/**
 * SLA Overview — aggregate SLA health for the current month. Parity with legacy
 * SLADashboardWidget. Real data from /sla/dashboard/overview.
 */
export function SlaOverviewWidget() {
  const { start, end, label } = useMemo(monthBounds, []);
  const { data, isLoading } = useSlaDashboardOverview(start, end);

  const uptime = data?.avg_uptime_percent ?? 0;
  const compliancePct =
    data && data.total_services_with_sla > 0
      ? Math.round((data.services_meeting_sla / data.total_services_with_sla) * 100)
      : 0;

  return (
    <WidgetCard
      title="SLA Overview"
      description={label}
      actions={
        <Button variant="ghost" size="sm">
          View details
        </Button>
      }
    >
      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Skeleton style={{ height: 64 }} />
          <Skeleton style={{ height: 80 }} />
        </div>
      ) : !data || data.total_services_with_sla === 0 ? (
        <EmptyState size="sm" title="No SLA services" description="No services have an SLA configured." />
      ) : (
        <>
          {/* Avg uptime — headline Progress bar, tone by threshold */}
          <Progress
            value={uptime}
            max={100}
            tone={uptimeTone(uptime)}
            size="lg"
            label="Average uptime"
            showValue
          />

          {/* Meeting vs Breaching + services-with-SLA count */}
          <StatGroup columns={3} style={{ marginTop: 'var(--vn-space-4)' }}>
            <Stat label="Services with SLA" value={String(data.total_services_with_sla)} />
            <Stat
              label="Meeting SLA"
              value={String(data.services_meeting_sla)}
              dotColor="var(--vn-success-solid)"
              hint={`${compliancePct}% compliant`}
            />
            <Stat
              label="Breaching SLA"
              value={String(data.services_breaching_sla)}
              dotColor={data.services_breaching_sla > 0 ? 'var(--vn-danger-solid)' : 'var(--vn-success-solid)'}
            />
          </StatGroup>

          {/* Downtime breakdown */}
          <div
            style={{
              marginTop: 'var(--vn-space-4)',
              paddingTop: 'var(--vn-space-3)',
              borderTop: '1px solid var(--vn-border-subtle)',
            }}
          >
            <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginBottom: 'var(--vn-space-2)' }}>
              Downtime breakdown
            </div>
            <StatGroup columns={3}>
              <Stat label="Provider" value={fmtMinutes(data.total_provider_downtime)} />
              <Stat label="Third-party" value={fmtMinutes(data.total_third_party_downtime)} />
              <Stat label="Scheduled" value={fmtMinutes(data.total_scheduled_downtime)} />
            </StatGroup>
          </div>

          {/* Incidents count — only when present */}
          {data.total_incidents > 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 'var(--vn-space-3)',
              }}
            >
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                Total incidents
              </span>
              <StatusPill tone="danger">{data.total_incidents}</StatusPill>
            </div>
          ) : null}
        </>
      )}
    </WidgetCard>
  );
}
